import type { QuickSearchHierarchyResult } from '@/types/searchTypes'
import clsx from 'clsx'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useOverflowList } from 'react-responsive-overflow-list'
import { FamilyCard } from '@/components/Families/FamilyCard'
import { mapQuickSearchFamily } from '@/components/QuickSearch/mappers/quickSearchFamilyMapper'
import { getQuickSearchResultLabel } from '@/components/QuickSearch/quickSearchLabels'
import { EmptyState } from './EmptyState'
import { HighlightMatch } from './HighlightMatch'

interface FamilyCardsSectionProps {
  query: string
  results: QuickSearchHierarchyResult[]
  onResultSelect: (result: QuickSearchHierarchyResult) => void
  enableOverflow?: boolean
  responsiveGrid?: boolean
  onOverflowCountChange?: (hiddenCount: number | null) => void
}

export function FamilyCardsSection({
  query,
  results,
  onResultSelect,
  enableOverflow = false,
  responsiveGrid = false,
  onOverflowCountChange,
}: Readonly<FamilyCardsSectionProps>) {
  const { i18n } = useTranslation()
  const cardResults = results.filter(result => !!result.id)
  const { containerRef, visibleCount, hiddenCount, phase } = useOverflowList<HTMLDivElement>({
    itemCount: cardResults.length,
    maxRows: 1,
  })

  useEffect(() => {
    onOverflowCountChange?.(enableOverflow && phase !== 'measuring' ? hiddenCount : null)
  }, [enableOverflow, hiddenCount, onOverflowCountChange, phase])

  if (results.length === 0) {
    return (
      <section className="quick-search__section">
        <EmptyState />
      </section>
    )
  }

  return (
    <section className="quick-search__section">
      <div
        ref={enableOverflow ? containerRef : undefined}
        className={clsx('quick-search__family-cards', {
          'quick-search__family-cards--overflow-enabled': enableOverflow,
          'quick-search__family-cards--responsive-grid': responsiveGrid,
        })}
      >
        {cardResults.map((result, index) => {
          const isVisible = !enableOverflow || phase === 'measuring' || index < visibleCount

          if (!isVisible)
            return null

          return (
            <FamilyCard
              key={result.id}
              family={mapQuickSearchFamily(result)}
              labelContent={(
                <HighlightMatch
                  text={getQuickSearchResultLabel(result.labels, i18n.language)}
                  query={query}
                />
              )}
              isSelected={false}
              selectCard={() => onResultSelect(result)}
            />
          )
        })}
      </div>
    </section>
  )
}
